import React, { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Input } from '@/app/components/form/Input';
import { PhoneInput } from '@/app/components/form/PhoneInput';
import type { PhoneInputValue } from '@/app/components/form/PhoneInput/types';
import { InfinityAsyncSelector } from '@/app/components/form/Selector/InfinityAsyncSelector';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import type { RelationshipData } from '@/types/clientDetailsApiTypes';

interface EmergencyContactProps {
  onUpdateFormData?: (fieldName: string, fieldValue: unknown) => void;
}

export const EmergencyContact = ({ onUpdateFormData }: EmergencyContactProps) => {
  const {
    control,
    register,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const phone = watch('primary_passenger.client.emergency_contact_phone');
  const handlePhoneChange = (value?: PhoneInputValue) => {
    setValue('primary_passenger.client.emergency_contact_phone', value?.phoneWithoutMask);
  };

  const handleRelationshipChange = (option: SelectorOption<string | number> | null) => {
    onUpdateFormData?.('primary_passenger.client.emergency_relationship_type_id', option?.value);
    setValue('primary_passenger.client.emergency_relationship_type_id', option?.value);
    setValue('primary_passenger.client.emergency_relationship', option);
  };

  const onMapOptions = useCallback((options: RelationshipData[]) => {
    return options.map((option) => ({
      value: option.id,
      label: option.name,
    }));
  }, []);

  const handleSaveOnBlur = (name: string, value: unknown) => {
    onUpdateFormData?.(name, value);
  };

  return (
    <div className="grid grid-cols-1 gap-5 mt-4">
      <Input
        {...register('primary_passenger.client.emergency_contact_name')}
        placeholder="Enter name"
        label="Name"
        className="w-full"
        errors={errors}
        intent="primaryWhite"
        onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <Input
          {...register('primary_passenger.client.emergency_contact_email')}
          placeholder="Enter email"
          label="Email"
          className="w-full"
          errors={errors}
          intent="primaryWhite"
          onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
        />

        <PhoneInput
          name="primary_passenger.client.emergency_contact_phone"
          value={phone || ''}
          onChange={handlePhoneChange}
          setError={setError}
          clearErrors={clearErrors}
          label="Phone number"
          placeholder="Enter phone number"
          errors={errors}
          onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
        />

        <Controller
          name="primary_passenger.client.emergency_relationship"
          control={control}
          render={({ field }) => (
            <InfinityAsyncSelector<RelationshipData>
              name="primary_passenger.client.emergency_relationship"
              url="/api/relationship-types"
              option={field.value}
              value={field.value ?? ''}
              mapOptions={onMapOptions}
              onChange={handleRelationshipChange}
              isClearable={false}
              queryKey={['relationship-type']}
              label="Relationship"
              placeholder="Select relationship"
              menuPosition="fixed"
              errors={errors}
            />
          )}
        />
      </div>
    </div>
  );
};
